import { useState, useRef, useEffect, createContext, useContext } from 'react'
import styled from 'styled-components'
import { Button } from 'react-bootstrap'
import { Stage, Layer, Rect } from 'react-konva'
import { useParams } from 'react-router-dom'
import { Edit2, X, Download } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useCreditCardMask, useBatchOCRMultiThread } from 'api'
import { useSelector, useDispatch } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import ConfirmationModal from './ConfirmationModal/ConfirmationModal'
import OcrReprocessConfirmModal from './OcrReprocessConfirmModal'
import { updatePaymentCard, selectPaymentCards } from 'slices/paymentCards'
import { api } from 'lib/axios'
import { QueryKeys } from 'lib'
import { getUserId } from 'lib/auth/tokenStorage'
import { buildCardCheckOverrides } from 'utils'
import moment from 'moment'
import { selectCustomerPreviousStatus, setMaskEditMode, incrementImageRefreshCounter, hideImagePreview } from 'slices/customer'
import { setModalLoading } from 'slices/loaderSlice'
import { useAutoStatusChange } from 'hooks/useAutoStatusChange'
const MaskEditorContext = createContext(null)

export const useMaskEditorContext = () => useContext(MaskEditorContext)

const isAbsoluteUrl = value =>
  typeof value === 'string' && /^(https?:)?\/\//i.test(value)

const CreditCardMaskEditorProvider = ({
  imagePreview,
  imageLoaded,
  imageRef,
  containerRef,
  maskedImageContainerRef,
  shouldMask,
  isOcrComplete = true,
  onToggleEditMode: onToggleEditModeExternal,
  onSaveSuccess,
  children
}) => {
  const dispatch = useDispatch()
  const paymentCards = useSelector(selectPaymentCards)
  const [isEditMode, setIsEditMode] = useState(false)
  const [drawing, setDrawing] = useState(false)
  const [rects, setRects] = useState([])
  const [currentRect, setCurrentRect] = useState(null)
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 })
  const [showOcrRequiredModal, setShowOcrRequiredModal] = useState(false)
  const queryClient = useQueryClient()
  const { handleBatchOCRMultiThread, isBatchOCRMultiThreadDataLoading } =
    useBatchOCRMultiThread()
  const userData = useSelector(state => state?.auth?.data)
  const currentUserInformation = userData?.current_user
  const previousStatus = useSelector(selectCustomerPreviousStatus)
  const { id: customerId } = useParams()
  const userRole = currentUserInformation?.affiliate_role || false
  const showToggleButton = userRole && userRole === 'auth_access'
  const currentCardIndex = paymentCards.findIndex(
    card => card.attributes.id === imagePreview.cardId
  )

  const getDocumentRevisionId = () => {
    const src = imagePreview.src || imagePreview.docId
    if (src && !isAbsoluteUrl(src)) return src

    if (!imagePreview.cardId || currentCardIndex === -1) return src

    const files = (paymentCards[currentCardIndex]?.files || []).filter(Boolean)
    const index = imagePreview.imageIndex ?? 0
    const file = files[index]
    return (
      file?.file_obj?.document_revision_id ||
      file?.document_revision_id ||
      file?.id ||
      src
    )
  }

  const getDocumentId = () => {
    if (!imagePreview.cardId || currentCardIndex === -1) return null

    const files = (paymentCards[currentCardIndex]?.files || []).filter(Boolean)
    const file = files[imagePreview.imageIndex ?? 0]
    if (!file) return null

    return (
      file?.file_obj?.document_id || file?.document_id || file?.id || null
    )
  }

  const isVaultImage = !!imagePreview?.isVaultSource
  const stageRef = useRef(null)
  const {
    handleUpdateCreditCardImageWithMask,
    handleApplyVaultRedaction,
    isSubmitting
  } = useCreditCardMask()
  const { checkAndUpdateStatus } = useAutoStatusChange()

  const handleToggleEditMode = () => {
    const newEditMode = !isEditMode
    if (newEditMode && !isOcrComplete) {
      setShowOcrRequiredModal(true)
      return
    }
    setIsEditMode(newEditMode)
    dispatch(setMaskEditMode(newEditMode))
    if (!newEditMode) {
      setRects([])
      setCurrentRect(null)
    }
    if (onToggleEditModeExternal) {
      onToggleEditModeExternal(newEditMode)
    }
  }

  const buildCurrentCardOcrPayload = () => {
    if (!customerId || !imagePreview.cardId || currentCardIndex === -1) {
      return null
    }

    const currentCard = paymentCards[currentCardIndex]
    const cardDocuments = (currentCard?.files || [])
      .filter(
        file =>
          file?.file_obj?.document_revision_id || file?.document_revision_id
      )
      .map(
        file =>
          file.file_obj?.document_revision_id || file?.document_revision_id
      )

    if (cardDocuments.length === 0) return null

    return {
      contact_id: customerId,
      documents: [
        {
          type: 'credit_card',
          document_id:
            cardDocuments.length === 1 ? cardDocuments[0] : cardDocuments,
          record_id: currentCard?.attributes?.id
        }
      ]
    }
  }

  const handleRunOcrFromModal = async () => {
    const payload = buildCurrentCardOcrPayload()
    if (!payload) {
      toast.error('No document available for OCR')
      return
    }

    try {
      const response = await handleBatchOCRMultiThread(payload)
      if (response) {
        dispatch(setModalLoading(true))
        try {
          const cardCheckOverrides = buildCardCheckOverrides(response)
          await checkAndUpdateStatus(cardCheckOverrides)
          await queryClient.invalidateQueries({
            queryKey: [QueryKeys.CUSTOMER_DETAILS, customerId]
          })
          setShowOcrRequiredModal(false)
          dispatch(hideImagePreview())
        } finally {
          dispatch(setModalLoading(false))
        }
      } else {
        toast.error('OCR failed. Please try again.')
      }
    } catch (error) {
      console.error('Credit card OCR error:', error)
      toast.error('OCR failed. Please try again.')
    }
  }

  useEffect(() => {
    setIsEditMode(false)
    setRects([])
    setCurrentRect(null)
    setDrawing(false)
    dispatch(setMaskEditMode(false))
    setShowOcrRequiredModal(false)
    if (onToggleEditModeExternal) {
      onToggleEditModeExternal(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imagePreview.src, imagePreview.imageIndex])

  useEffect(() => {
    return () => {
      dispatch(setMaskEditMode(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (imageLoaded && isEditMode) {
      const updateSize = () => {
        let img = null
        let container = null

        if (shouldMask) {
          container = maskedImageContainerRef?.current
        } else {
          img = imageRef?.current
          container = containerRef?.current
        }

        if (!container) return

        if (!img && container) {
          img = container.querySelector('img')
        }

        if (img) {
          setStageSize({
            width: img.offsetWidth || img.clientWidth || img.naturalWidth,
            height: img.offsetHeight || img.clientHeight || img.naturalHeight
          })
        } else {
          setStageSize({
            width: container.offsetWidth,
            height: container.offsetHeight
          })
        }
      }

      const timeoutId = setTimeout(updateSize, 100)
      window.addEventListener('resize', updateSize)

      return () => {
        clearTimeout(timeoutId)
        window.removeEventListener('resize', updateSize)
      }
    }
  }, [
    imageLoaded,
    isEditMode,
    shouldMask,
    imageRef,
    containerRef,
    maskedImageContainerRef
  ])

  const handleMouseDown = e => {
    if (!isEditMode) return
    // Only the primary button draws masks; secondary buttons would leave a stray rect
    if (e.evt && e.evt.button !== 0) return
    const stage = e.target.getStage()
    const pos = stage.getPointerPosition()
    setDrawing(true)
    setCurrentRect({
      x: pos.x,
      y: pos.y,
      width: 0,
      height: 0
    })
  }

  const handleMouseMove = e => {
    if (!isEditMode || !drawing || !currentRect) return
    const stage = e.target.getStage()
    const point = stage.getPointerPosition()

    const newRect = {
      x: Math.min(currentRect.x, point.x),
      y: Math.min(currentRect.y, point.y),
      width: Math.abs(point.x - currentRect.x),
      height: Math.abs(point.y - currentRect.y)
    }

    setCurrentRect(newRect)
  }

  const handleMouseUp = () => {
    if (
      drawing &&
      currentRect &&
      currentRect.width > 5 &&
      currentRect.height > 5
    ) {
      setRects([...rects, currentRect])
    }
    setDrawing(false)
    setCurrentRect(null)
  }

  const clearMasks = () => {
    setRects([])
    setCurrentRect(null)
  }

  const getMaskCoordinates = () => {
    if (rects.length === 0) {
      return null
    }

    let imgWidth = 0
    let imgHeight = 0
    let imgElement = null

    if (shouldMask) {
      const container = maskedImageContainerRef?.current
      if (container) imgElement = container.querySelector('img')
    } else {
      imgElement = imageRef?.current
    }

    if (imgElement) {
      imgWidth = imgElement.naturalWidth || imgElement.width
      imgHeight = imgElement.naturalHeight || imgElement.height
    }

    const displayWidth = stageSize.width || imgElement?.offsetWidth || 800
    const displayHeight = stageSize.height || imgElement?.offsetHeight || 600

    const useNaturalDimensions = imgWidth > 0 && imgHeight > 0
    const scaleX =
      useNaturalDimensions && displayWidth > 0 ? imgWidth / displayWidth : 1
    const scaleY =
      useNaturalDimensions && displayHeight > 0 ? imgHeight / displayHeight : 1

    return rects.map(rect => ({
      x: Math.round(rect.x * scaleX),
      y: Math.round(rect.y * scaleY),
      width: Math.round(rect.width * scaleX),
      height: Math.round(rect.height * scaleY)
    }))
  }

  const generateApiPayload = () => {
    if (rects.length === 0) {
      return null
    }

    try {
      const maskCoordinates = getMaskCoordinates()

      if (!maskCoordinates) {
        return null
      }

      const apiPayload = {
        document_revision_id: getDocumentRevisionId(),
        mask_coordinates: maskCoordinates
      }

      return apiPayload
    } catch (error) {
      return null
    }
  }

  const processRedactionSuccess = (response, { logActivity = false } = {}) => {
    const responseIsLocked = response?.contact_state?.is_locked_c

    if (response?.success === true && currentCardIndex !== -1) {
      dispatch(
        updatePaymentCard({
          cardIndex: currentCardIndex,
          field: 'redaction_verified',
          value: "1"
        })
      )
      dispatch(
        updatePaymentCard({
          cardIndex: currentCardIndex,
          field: 'redaction_message',
          value: ''
        })
      )
      dispatch(incrementImageRefreshCounter())

      if (logActivity) {
        const now = moment()
        const utcMoment = now.utc()
        const formattedTime = utcMoment.format('YYYY-MM-DD HH:mm:ss')
        const body = {
          data: {
            type: 'gb_action_tracking_logs',
            attributes: {
              affiliate_member_id: getUserId(),
              action: 'manual_redaction_success',
              action_time: formattedTime,
              parent_id: customerId
            }
          }
        }
        api.post('/Api/V8/module', body).catch(error => {
          console.error('Error saving manual redaction success activity:', error)
        })
      }

      const currentCardId =
        imagePreview?.cardId || paymentCards[currentCardIndex]?.attributes?.id

      if (currentCardId) {
        checkAndUpdateStatus(
          { [currentCardId]: { redaction_verified: '1' } },
          { is_locked_c: responseIsLocked }
        )
      }
    }
  }

  const handleSaveVaultMask = async maskCoordinates => {
    if (!imagePreview.cardId) {
      throw new Error('Card id is required to apply vault redaction')
    }

    const documentId = getDocumentId()
    if (!documentId) {
      throw new Error('Document id is required to apply vault redaction')
    }

    const apiPayload = {
      card_id: imagePreview.cardId,
      image_index: imagePreview.imageIndex ?? 0,
      agent_id: getUserId(),
      document_id: documentId,
      redaction_metadata: {
        polygons: maskCoordinates
      }
    }

    const response = await handleApplyVaultRedaction(apiPayload)

    processRedactionSuccess(response, { logActivity: false })

    if (onSaveSuccess) {
      onSaveSuccess(response)
    }

    return response
  }

  const handleSaveMask = async () => {
    if (rects.length === 0) {
      return null
    }

    try {
      const maskCoordinates = getMaskCoordinates()

      if (!maskCoordinates) {
        return null
      }

      if (isVaultImage) {
        return await handleSaveVaultMask(maskCoordinates)
      }

      const documentRevisionId = getDocumentRevisionId()

      if (!documentRevisionId || isAbsoluteUrl(documentRevisionId)) {
        throw new Error('Document revision ID is required')
      }

      const apiPayload = {
        document_revision_id: documentRevisionId,
        mask_coordinates: maskCoordinates,
        redaction_verified: 1,
        redaction_message: 'Redaction verified',
        previous_status_c: previousStatus,
        agent_id: getUserId(),
        is_locked_c: 0
      }

      if (imagePreview.cardId) {
        apiPayload.card_id = imagePreview.cardId
      }

      const response = await handleUpdateCreditCardImageWithMask(apiPayload)

      processRedactionSuccess(response)

      if (onSaveSuccess) {
        onSaveSuccess(response)
      }

      return response
    } catch (error) {
      throw error
    }
  }

  const contextValue = {
    isEditMode,
    drawing,
    rects,
    currentRect,
    stageSize,
    stageRef,
    handleToggleEditMode,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    clearMasks,
    generateApiPayload,
    handleSaveMask,
    isSubmitting,
    showToggleButton,
    isVaultImage
  }

  return (
    <MaskEditorContext.Provider value={contextValue}>
      {children}
      <OcrReprocessConfirmModal
        open={showOcrRequiredModal}
        onClose={() => {
          if (isBatchOCRMultiThreadDataLoading) return
          setShowOcrRequiredModal(false)
        }}
        onConfirm={handleRunOcrFromModal}
        title="OCR Incomplete"
        message="OCR Has not been run on selected card"
        subMessage="Confirm you want to run OCR on this card"
        confirmText="OCR"
        loading={isBatchOCRMultiThreadDataLoading}
      />
    </MaskEditorContext.Provider>
  )
}

const CreditCardMaskEditorControls = () => {
  const ctx = useMaskEditorContext()
  if (!ctx) {
    return null
  }

  const {
    isEditMode,
    rects,
    handleToggleEditMode,
    clearMasks,
    handleSaveMask,
    isSubmitting,
    showToggleButton
  } = ctx
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  if (!handleToggleEditMode) {
    return null
  }

  const handleSaveClick = async () => {
    if (rects && rects.length > 0) {
      setShowConfirmModal(true)
    }
  }

  const handleConfirmSave = async () => {
    setShowConfirmModal(false)
    try {
      await handleSaveMask()
    } catch (error) {
      console.error('Error saving mask:', error)
    }
  }

  const handleCancelSave = () => {
    setShowConfirmModal(false)
  }

  return (
    <>
      {showToggleButton && (
        <EditButton
          onClick={handleToggleEditMode}
          title={isEditMode ? 'Exit Edit Mode' : 'Edit Image'}
        >
          {isEditMode ? <X size={18} /> : <Edit2 size={18} />}
        </EditButton>
      )}
      {isEditMode && clearMasks && handleSaveMask && (
        <ButtonGroup>
          <ClearButton onClick={clearMasks} variant="secondary" size="sm">
            Clear Masks
          </ClearButton>
          <ApiPayloadButton
            onClick={handleSaveClick}
            variant="primary"
            size="sm"
            disabled={!rects || rects.length === 0 || isSubmitting}
          >
            <Download size={16} style={{ marginRight: '4px' }} />
            {isSubmitting ? 'Saving...' : 'Save'}
          </ApiPayloadButton>
        </ButtonGroup>
      )}
      <ConfirmationModal
        open={showConfirmModal}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
        title="Confirm Save"
        message="Could you please confirm that the redaction has been applied correctly? Please note that this action cannot be undone."
      />
    </>
  )
}

const CreditCardMaskEditorCanvas = ({ imageLoaded }) => {
  const ctx = useMaskEditorContext()
  if (!ctx) return null

  const {
    isEditMode,
    rects,
    currentRect,
    stageSize,
    stageRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp
  } = ctx

  if (
    !isEditMode ||
    !imageLoaded ||
    stageSize.width <= 0 ||
    stageSize.height <= 0
  ) {
    return null
  }

  return (
    <CanvasOverlay onContextMenu={e => e.preventDefault()}>
      <Stage
        ref={stageRef}
        width={stageSize.width}
        height={stageSize.height}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        style={{ cursor: 'crosshair' }}
      >
        <Layer>
          {rects.map((rect, i) => (
            <Rect
              key={i}
              x={rect.x}
              y={rect.y}
              width={rect.width}
              height={rect.height}
              fill="#000000"
            />
          ))}
          {currentRect && (
            <Rect
              x={currentRect.x}
              y={currentRect.y}
              width={currentRect.width}
              height={currentRect.height}
              fill="#000000"
            />
          )}
        </Layer>
      </Stage>
    </CanvasOverlay>
  )
}

export {
  CreditCardMaskEditorProvider,
  CreditCardMaskEditorControls,
  CreditCardMaskEditorCanvas
}

const EditButton = styled.button`
  position: absolute;
  top: 13px;
  left: 20px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #ddd;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  svg {
    color: #333;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 50px;
`

const ClearButton = styled(Button)`
  /* Button styles handled by react-bootstrap */
`

const ApiPayloadButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffc107 !important;
  border-color: #ffc107 !important;
  color: #000 !important;

  &:hover {
    background-color: #ffb300 !important;
    border-color: #ffb300 !important;
    color: #000 !important;
  }

  &:disabled {
    background-color: #ffc107 !important;
    border-color: #ffc107 !important;
    opacity: 0.6;
  }
`

const CanvasOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
  z-index: 1000;
`

export default CreditCardMaskEditorProvider
